import { pdf } from '@react-pdf/renderer';
import { ProgramCertificateDocument } from '@/components/certificates/ProgramCertificateDocument';
import type { ProgramCertificateMetadata } from '@court-sync/shared';
import React from 'react';

/** Fetch image with CORS; embed as data URL so react-pdf can render without cross-origin Image failures. */
async function resolveImageUrlToDataUrl(url: string | undefined): Promise<string | undefined> {
  if (!url?.trim()) return undefined;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const slice = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...slice);
    }
    const base64 = globalThis.btoa(binary);
    const mime = blob.type || 'image/png';
    return `data:${mime};base64,${base64}`;
  } catch {
    return undefined;
  }
}

export async function generateProgramCertificatePdfBlob(
  meta: ProgramCertificateMetadata
): Promise<globalThis.Blob> {
  const [academyLogoUrl, certificateSignatureImageUrl] = await Promise.all([
    resolveImageUrlToDataUrl(meta.academyLogoUrl),
    resolveImageUrlToDataUrl(meta.certificateSignatureImageUrl),
  ]);
  const metaForPdf: ProgramCertificateMetadata = {
    ...meta,
    academyLogoUrl,
    certificateSignatureImageUrl,
  };
  const doc = React.createElement(ProgramCertificateDocument, { meta: metaForPdf });
  return pdf(doc as React.ReactElement).toBlob();
}

export function downloadBlob(blob: globalThis.Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
